import type { SupportStep } from './support-step';

export interface SupportFileSelectionEvidence {
  actionId: string;
  planRunId: string;
  taskId: string;
  extension: string;
  sizeBytes: number;
  sha256: string;
}

type SelectionResult = { evidence: SupportFileSelectionEvidence } | { error: string };

/** Ask the customer to choose a file, then validate it before one host assignment. */
export async function selectSupportFile(
  step: SupportStep,
  hostInput: HTMLInputElement,
  actionId: string,
  planRunId: string,
  signal: AbortSignal | undefined,
  afterAssignAuthorized: () => boolean,
  canAssign: () => boolean,
): Promise<SelectionResult> {
  if (!step.file || !actionId || !planRunId || !canAssign() || hostInput.files?.length) {
    return { error: 'File selection is unavailable or a file is already selected' };
  }
  const panel = document.createElement('div');
  panel.setAttribute('data-floe-widget', 'file-selection');
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', 'Choose a file for this support task');
  panel.style.cssText = 'position:fixed;right:16px;bottom:16px;z-index:2147483647;padding:16px;max-width:320px;background:#fff;color:#111;border:2px solid #222;border-radius:8px;box-shadow:0 4px 20px #0005;font:14px sans-serif;';
  const explanation = document.createElement('p');
  explanation.textContent = `Choose a file for ${location.host}. Review its name and size, then select Supply this file. Floe will supply it to the upload control only after you confirm. This page may access a file you select before confirmation.`;
  const picker = document.createElement('input');
  picker.type = 'file';
  picker.accept = step.file.extensions.join(',');
  picker.setAttribute('aria-label', 'Choose file');
  // Host CRMs may hide every file input; this customer-owned picker must remain usable.
  picker.style.setProperty('display', 'block', 'important');
  picker.style.setProperty('visibility', 'visible', 'important');
  const selectedDetails = document.createElement('p');
  selectedDetails.setAttribute('aria-live', 'polite');
  selectedDetails.textContent = 'No file selected.';
  const supply = document.createElement('button');
  supply.type = 'button';
  supply.textContent = 'Supply this file';
  supply.disabled = true;
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.textContent = 'Cancel file selection';
  panel.append(explanation, picker, selectedDetails, supply, cancel);
  document.body.append(panel);

  const timeoutMs = Math.min(120000, Math.max(10000, step.timeoutMs ?? 90000));
  const selected = await new Promise<File | null>((resolve) => {
    let finished = false;
    let armedByUser = false;
    let reviewedFile: File | null = null;
    const finish = (file: File | null) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      picker.removeEventListener('change', onChange);
      picker.removeEventListener('click', onActivate);
      picker.removeEventListener('keydown', onActivate);
      cancel.removeEventListener('click', onCancel);
      signal?.removeEventListener('abort', onCancel);
      panel.remove();
      resolve(file);
    };
    const onActivate = (event: Event) => { if (event.isTrusted) armedByUser = true; };
    const onChange = (event: Event) => {
      reviewedFile = event.isTrusted && armedByUser && picker.files?.length === 1 ? picker.files[0] : null;
      supply.disabled = !reviewedFile;
      selectedDetails.textContent = reviewedFile
        ? `Selected file: ${reviewedFile.name} (${reviewedFile.size} bytes). Confirm to supply it to ${location.host}.`
        : 'No file selected.';
    };
    const onSupply = (event: Event) => {
      if (!event.isTrusted || !reviewedFile || picker.files?.length !== 1 || picker.files[0] !== reviewedFile) {
        return;
      }
      finish(reviewedFile);
    };
    const onCancel = () => finish(null);
    const timer = window.setTimeout(onCancel, timeoutMs);
    picker.addEventListener('change', onChange);
    picker.addEventListener('click', onActivate);
    picker.addEventListener('keydown', onActivate);
    supply.addEventListener('click', onSupply);
    cancel.addEventListener('click', onCancel);
    signal?.addEventListener('abort', onCancel, { once: true });
    if (signal?.aborted || !canAssign()) finish(null);
  });
  if (!selected || signal?.aborted || !canAssign()) return { error: 'File selection was cancelled or changed' };
  const extension = selected.name.slice(selected.name.lastIndexOf('.')).toLowerCase();
  if (!step.file.extensions.includes(extension) || selected.size <= 0 || selected.size > step.file.maxBytes) {
    return { error: 'The selected file type or size is outside the approved limits' };
  }
  if (!crypto.subtle || !canAssign()) return { error: 'File verification is unavailable' };
  const digest = await crypto.subtle.digest('SHA-256', await selected.arrayBuffer());
  const sha256 = [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('');
  if (signal?.aborted || !canAssign() || hostInput.files?.length) {
    return { error: 'File selection changed before assignment' };
  }
  try {
    // The host may have populated its own control while the customer chose or
    // while SHA-256 was computed. Never replace that file with ours.
    if (hostInput.files?.length) return { error: 'The host file input changed before assignment' };
    const transfer = new DataTransfer();
    transfer.items.add(selected);
    hostInput.files = transfer.files;
    const assigned = hostInput.files?.[0];
    if (!assigned || hostInput.files?.length !== 1 || assigned.name !== selected.name || assigned.size !== selected.size) {
      return { error: 'The host file input did not accept the selected file' };
    }
    hostInput.dispatchEvent(new Event('input', { bubbles: true }));
    hostInput.dispatchEvent(new Event('change', { bubbles: true }));
    if (signal?.aborted || !afterAssignAuthorized() || hostInput.files?.length !== 1) {
      return { error: 'The host file input changed after selection' };
    }
    const readback = hostInput.files[0];
    if (readback.size !== selected.size || readback.name !== selected.name ||
        !crypto.subtle) return { error: 'The host file input changed after selection' };
    const readbackDigest = await crypto.subtle.digest('SHA-256', await readback.arrayBuffer());
    const readbackHash = [...new Uint8Array(readbackDigest)].map(byte => byte.toString(16).padStart(2, '0')).join('');
    if (signal?.aborted || !afterAssignAuthorized() || hostInput.files?.length !== 1 ||
        hostInput.files[0] !== readback || readbackHash !== sha256) {
      return { error: 'The host file input changed after selection' };
    }
    return { evidence: { actionId, planRunId, taskId: step.taskId, extension, sizeBytes: selected.size, sha256 } };
  } catch {
    return { error: 'The host file input did not accept the selected file' };
  }
}
